<template>
  <FriedDropdown
    class="chat-header-dropdown"
    :same-width-as-ref="true"
    :placement="Placement.BottomStart"
    @update:open="showParticipants = $event"
  >
    <template #trigger>
      <div class="chat-header-content">
        <div class="chat-header">
          <span class="chat-header-icon-container">
            <FriedIcon
              :class="[
                'icon chat-header-icon',
                { rotate: showParticipants, 'disable-click': hasRecipients },
              ]"
              icon="chevron-down"
            />
          </span>
          <FriedH4 class="chat-header-title">{{ headerText }}</FriedH4>
        </div>
        <FriedIconButton
          data-external="signing-page-close-chat-button"
          class="close"
          @click="handleClose"
        >
          <FriedIcon class="icon close-icon" icon="close" />
        </FriedIconButton>
      </div>
    </template>
    <template #content>
      <div class="chat-participant-container">
        <div
          v-for="participant in chatParticipants"
          :key="participant.id"
          class="participant-container"
        >
          <FriedAvatar
            :thumb-url="participant.thumbUrl"
            :name="participant.fullName || participant.email"
          />
          <div class="participant-information">
            <FriedParagraph class="name">{{ getDisplayName(participant) }}</FriedParagraph>
            <FriedParagraph v-if="participant.email">{{ participant.email }}</FriedParagraph>
            <FriedParagraph v-else-if="participant.mobile">{{ participant.mobile }}</FriedParagraph>
          </div>
        </div>
      </div>
    </template>
  </FriedDropdown>
</template>

<script lang="ts">
import type { SigningPageUser } from '@getaccept/lib-shared-new/src/types/signing-page-user';
import type { SigningPageRecipient } from '@getaccept/lib-shared-new/src/types/signing-page-recipient';
import type { PropType } from 'vue';
import { computed, ref, defineComponent } from 'vue';
import { Placement } from '@getaccept/fried-tofu';
import { ChatHelper } from '../helpers/chat.helper';
import type { ChatParticipant } from '../types/chat-participant';

export default defineComponent({
  props: {
    sender: {
      type: Object as PropType<SigningPageUser>,
      required: true,
    },
    recipients: {
      type: Array as PropType<SigningPageRecipient[]>,
      required: true,
    },
    recipientId: {
      type: String,
      required: true,
    },
  },
  emits: ['close'],
  setup(props, { emit }) {
    const showParticipants = ref(false);

    const hasRecipients = computed((): boolean => props.recipients?.length > 0);

    const chatParticipants = computed(() =>
      ChatHelper.getChatParticipants(props.sender, props.recipients, props.recipientId)
    );

    const headerText = computed((): string => {
      if (!chatParticipants.value) {
        return '';
      }
      return `${chatParticipants.value
        .map((participant: ChatParticipant, index: number) => {
          const displayName = ChatHelper.getDisplayName(participant, true);
          return `${displayName}${ChatHelper.addPunctuationMark(
            index,
            chatParticipants.value.length
          )}`;
        })
        .join(' ')}`;
    });

    const handleClose = () => {
      emit('close');
    };

    return {
      getDisplayName: ChatHelper.getDisplayName,
      showParticipants,
      hasRecipients,
      chatParticipants,
      headerText,
      handleClose,
      Placement,
    };
  },
});
</script>

<style lang="scss" scoped>
.chat-header-dropdown {
  :deep(.floater-reference-container) {
    width: 100%;
  }
}

.chat-header-content {
  width: 100%;
  height: var(--chat-header-height);
  display: flex;
  justify-content: flex-start;
  padding: var(--spacing-100) var(--spacing-50);
  box-sizing: border-box;
  background-color: var(--gray-bg);
  align-items: center;
  backdrop-filter: blur(6px);

  .chat-header {
    cursor: pointer;
    height: 100%;
    min-height: 2.25rem;
    max-width: 95%;
    display: flex;
    align-items: center;
    overflow: hidden;

    &.disable-click {
      cursor: initial;
    }

    .chat-header-icon-container {
      margin: var(--spacing-50);

      .chat-header-icon {
        transition: transform 0.3s ease-in-out;

        &.rotate {
          transform: rotate(180deg);
        }
      }
    }

    .chat-header-title {
      padding: var(--spacing-25);
      padding-right: var(--spacing-150);
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      font-weight: bold;
    }
  }

  .close {
    cursor: pointer;
    position: absolute;
    right: var(--spacing-50);
    top: var(--spacing-25);
    padding: var(--spacing-50);
  }
}

.chat-participant-container {
  background: var(--gray-bg);
  padding-bottom: var(--spacing-100);
  overflow: auto;
  max-height: 23rem;
  border-bottom: 1px solid var(--gray-89);
  z-index: 10000;

  .participant-container {
    padding: var(--spacing-50) var(--spacing-150);
    display: flex;

    .participant-information {
      padding: 0 var(--spacing-100);
      display: flex;
      flex-direction: column;
      justify-content: center;

      .name {
        font-weight: 600;
      }
    }
  }
}
</style>
